import { Box, Divider } from '@mui/material';
import { useTaskNotes } from 'components/featured/Tasks/hooks/useTasksNotes';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TaskService } from 'service/task.service';
import zeplinColor from '../../../theme/Color';
import CustomAddTask from '../CustomAddTask/CustomAddTask';
import CustomAlert from '../CustomAlert/CustomAlert';
import CustomDrawer from '../CustomDrawer/CustomDrawer';
import CustomNotes from '../CustomNotes';
import { ConfirmationModal } from './components/ConfirmationModal';
import { HeaderActions } from './components/HeaderActions';
import { SalesforceAuthPopover } from './components/SalesforceAuthPopover';
import { TaskAttachments } from './components/TaskAttachments';
import { TaskFields } from './components/TaskFields';
import { TaskHeader } from './components/TaskHeader';
import style from './CustomViewTask.module.css';
import { useSalesforceAuth } from './hooks/useSalesforceAuth';
import { useTaskActions } from './hooks/useTaskActions';
import { useTaskFiles } from './hooks/useTaskFiles';

const CustomViewTask = ({
  setDrawerState,
  drawerState,
  selectedData,
  setEditDone,
  setDeleted,
  setSelected,
  isFromTasksTab,
  initialEditMode = false,
  onUpdate,
}: {
  setDrawerState: React.Dispatch<React.SetStateAction<boolean>>;
  drawerState: boolean;
  selectedData: any;
  setEditDone: React.Dispatch<React.SetStateAction<boolean>>;
  setDeleted: React.Dispatch<React.SetStateAction<string>>;
  setSelected: React.Dispatch<React.SetStateAction<any>>;
  isFromTasksTab?: boolean;
  initialEditMode?: boolean;
  onUpdate?: () => void;
}) => {
  const location = useLocation();
  const { sfEdit, sfMarked, sfDelete, sfEditSelected, isSalesforceAuthenticated } =
    useSalesforceAuth();
  const [edit, setEdit] = useState(initialEditMode);
  const [openModal, setOpenModal] = useState(false);
  const [taskAnchorEl, setTaskAnchorEl] = useState<HTMLDivElement | null>(null);
  const [action, setAction] = useState('');
  const { alertState, startOperation, completeOperation, getAlertContent, setAlertState } =
    useAlertStatus();

  const handleAttachmentUpload = useCallback(
    async (urls: string[]) => {
      if (!selectedData?.id || urls.length === 0) return;

      try {
        let existingAttachments: any[] = [];

        if (selectedData?.attachments) {
          if (Array.isArray(selectedData.attachments)) {
            existingAttachments = selectedData.attachments;
          } else if (
            typeof selectedData.attachments === 'object' &&
            'added' in selectedData.attachments
          ) {
            existingAttachments = Array.isArray(selectedData.attachments.added)
              ? selectedData.attachments.added
              : [];
          } else if (typeof selectedData.attachments === 'string') {
            existingAttachments = [selectedData.attachments];
          }
        }

        const allAttachments = [
          ...existingAttachments.filter((att: any) => typeof att === 'string'),
          ...urls,
        ];

        const updateData: any = {
          ...(selectedData?.subject && { subject: selectedData.subject }),
          status: Array.isArray(selectedData?.status)
            ? selectedData.status[0]
            : selectedData?.status || 'Not Started',
          attachments: {
            added: allAttachments, // Send all attachments (existing + new)
            removed: [],
          },
        };

        await TaskService.updateTask(selectedData.id, updateData);

        if (setSelected) {
          setSelected((prev: any) => ({
            ...prev,
            attachments: allAttachments,
          }));
        }

        completeOperation('upload', 'Attachments uploaded successfully');

        onUpdate?.();
      } catch (err: any) {
        completeOperation(
          'upload',
          `${err?.response?.data?.error} || 'Failed to upload attachments`,
          true,
        );
      }
    },
    [selectedData, onUpdate, setSelected, completeOperation],
  );

  const { selectedFiles, onDrop, resetFiles, deleteFile, setSelectedFiles } = useTaskFiles(
    selectedData?.attachments,
    handleAttachmentUpload,
  );

  const handleAttachmentDelete = useCallback(
    async (index: number) => {
      if (!selectedData?.id) return;

      const fileToRemove = selectedFiles[index];
      const isExistingFile = typeof fileToRemove === 'string';
      const originalFiles = [...selectedFiles];

      // Remove from local state optimistically
      deleteFile(index);

      try {
        const remainingAttachments = originalFiles.filter((_, ind) => ind !== index);
        const removedAttachments = isExistingFile ? [fileToRemove] : [];

        const updateData: any = {
          ...(selectedData?.subject && { subject: selectedData.subject }),
          status: Array.isArray(selectedData?.status)
            ? selectedData.status[0]
            : selectedData?.status || 'Not Started',
          attachments: {
            added: remainingAttachments.filter((att: any) => typeof att === 'string'),
            removed: removedAttachments,
          },
        };

        await TaskService.updateTask(selectedData.id, updateData);

        if (setSelected) {
          setSelected((prev: any) => ({
            ...prev,
            attachments: remainingAttachments.filter((att: any) => typeof att === 'string'),
          }));
        }

        completeOperation('delete', 'Attachment deleted successfully');
        onUpdate?.();
      } catch (err: any) {
        // Revert local state on error
        setSelectedFiles(originalFiles);
        completeOperation(
          'delete',
          err?.response?.data?.error || 'Failed to delete attachment',
          true,
        );
      }
    },
    [
      selectedData,
      selectedFiles,
      deleteFile,
      setSelectedFiles,
      setSelected,
      completeOperation,
      onUpdate,
    ],
  );
  const { loading, deleteTask } = useTaskActions({
    selectedData,
    setDrawerState,
    setDeleted,
    completeOperation,
  });

  const onClose = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    resetFiles();
    setDrawerState(open);
    sessionStorage.removeItem('openTask');
    sessionStorage.removeItem('sf_path');
    sessionStorage.removeItem('sf_task_status');
    sessionStorage.removeItem('sf_redirect');
    sessionStorage.removeItem('sf_edit_selected');
    sessionStorage.removeItem('sf_edit');
    sessionStorage.removeItem('sf_delete');
    sessionStorage.removeItem('notes_id');
    sessionStorage.removeItem('notes_content');
    sessionStorage.removeItem('triggerTaskEdit');
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  const handleOpen = () => setOpenModal((prev) => !prev);

  const handleTaskCreateClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!location?.pathname?.includes('huddle') && selectedData?.source === 'SALESFORCE') {
      if (isSalesforceAuthenticated) {
        setEdit(true);
      } else {
        setTaskAnchorEl(event.currentTarget);
        setAction('edit');
      }
    } else {
      setEdit(true);
    }
  };

  useEffect(() => {
    if (!drawerState) {
      setEdit(false);
    } else if (initialEditMode) {
      setEdit(true);
    }
  }, [drawerState, initialEditMode]);

  const handleModalConfirm = async () => {
    await deleteTask(selectedData?.id);
    setOpenModal(false);
    setDrawerState(false);
  };

  useEffect(() => {
    if (sfEdit === 'true' && isSalesforceAuthenticated && !edit) {
      setEdit((prev) => !prev);
    } else if (sfDelete === 'true' && isSalesforceAuthenticated && !openModal) {
      handleOpen();
    } else if (
      sfMarked === 'true' &&
      isSalesforceAuthenticated &&
      !openModal &&
      sfEditSelected &&
      JSON.parse(sfEditSelected || '')?.id === selectedData?.id
    ) {
      handleOpen();
    }
  }, [
    sfEdit,
    isSalesforceAuthenticated,
    sfDelete,
    sfMarked,
    sfEditSelected,
    selectedData?.id,
    edit,
    openModal,
  ]);

  const manager = useTaskNotes(selectedData.id, startOperation, completeOperation);

  return edit ? (
    <CustomAddTask
      handleContinueAdd={() => {}}
      handleModalClose={() => {}}
      modalOpen={false}
      setDrawerState={setDrawerState}
      drawerState={drawerState}
      edit={edit}
      setEdit={setEdit}
      setEditDone={setEditDone}
      data={selectedData}
      hideSourceChip
    />
  ) : (
    <CustomDrawer state={drawerState} onClose={onClose(false)}>
      <Box sx={{ width: { xs: '427px', xl: '527px' }, position: 'relative', overflowY: 'scroll' }}>
        <Box
          sx={{ position: 'sticky', top: 0, backgroundColor: zeplinColor.Neutral1, zIndex: '1' }}
        >
          <HeaderActions
            onEdit={handleTaskCreateClick}
            onDelete={handleOpen}
            onClose={onClose(false)}
          />
          <Divider
            component='div'
            sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
          />
        </Box>
        <SalesforceAuthPopover
          anchorEl={taskAnchorEl}
          setAnchorEl={setTaskAnchorEl}
          action={action}
          selectedData={selectedData}
          onClose={handleClose}
        />

        <Box sx={{ padding: { xs: '20px', xl: '24px' } }}>
          <Box sx={{ display: 'flex', gap: { xs: '16px', xl: '24px' }, flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', gap: { xs: '16px', xl: '32px' }, flexDirection: 'column' }}>
              <TaskHeader selectedData={selectedData} isFromTasksPage={isFromTasksTab} />
              <TaskFields
                selectedData={selectedData}
                loading={loading}
                completeOperation={completeOperation}
                startOperation={startOperation}
                onUpdate={(updatedTaskData) => {
                  if (updatedTaskData && setSelected) {
                    setSelected(updatedTaskData);
                  }
                  setEditDone(true);
                  onUpdate?.();
                }}
              />
              <Divider
                component='div'
                sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
              />
            </Box>
            {selectedData?.link && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
                  <img
                    className={style['field-icon']}
                    src={'/assets/link-attachment.svg'}
                    alt='logo'
                    style={{ objectFit: 'fill' }}
                  />
                  <Box component='span' sx={{ fontSize: '14px', color: zeplinColor.BasicBlack }}>
                    Link
                  </Box>
                </Box>
                <a
                  href={selectedData?.link}
                  style={{ textDecoration: 'none' }}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <Box component='span' sx={{ fontSize: '14px', color: zeplinColor.Brand1 }}>
                    {selectedData?.link}
                  </Box>
                </a>
              </Box>
            )}
            <TaskAttachments
              files={selectedFiles}
              onFileSelect={async (files) => {
                await onDrop(files);
              }}
              onFileDelete={handleAttachmentDelete}
            />
            <Divider
              component='div'
              sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
            />
          </Box>
        </Box>

        <Box sx={{ padding: '0 24px 24px 24px' }}>
          <CustomNotes manager={manager} />
        </Box>
        <ConfirmationModal
          open={openModal}
          onClose={handleClose}
          onConfirm={handleModalConfirm}
          loading={loading}
        />
      </Box>
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </CustomDrawer>
  );
};
export default CustomViewTask;
